import { Unit } from "./measurementEnums";

export const METERS_PER_NAUTICAL_MILE = 1852;

/**
 * A finished measurement in the result list: either the graphics a draw tool produced, or a
 * measurement view model.
 */
export type MeasureEntry = Array<unknown> | { vm?: unknown };

/**
 * Adds a measurement view model to the result list, unless it is already in there.
 *
 * The view models re-enter their "measured" state when a finished measurement is edited, so the
 * same one can be reported more than once. Returning the list untouched in that case keeps the
 * measurements that were made after it.
 */
export const appendMeasurement = <T extends MeasureEntry>(current: Array<T>, vm: unknown) => {
    const isTracked = current.some((entry) => !Array.isArray(entry) && entry?.vm === vm);
    if (isTracked) return current;

    return [...current, { vm } as T];
};

/**
 * Formats a length given in metres for display, in the unit the user selected.
 *
 * Always takes the raw length in metres rather than an already formatted label: the view models
 * and graphic attributes all carry metres, and re-parsing a localised label to convert it loses
 * the magnitude whenever the label was written in kilometres.
 */
export const formatLength = (lengthMeters: number, selectedUnit: string | null) => {
    if (selectedUnit === Unit.Nautiskemil) {
        return `${(lengthMeters / METERS_PER_NAUTICAL_MILE).toFixed(2)} Nm`;
    }

    return lengthMeters < 1000 ? `${lengthMeters.toFixed(2)} m` : `${(lengthMeters / 1000).toFixed(2)} km`;
};

/**
 * Swaps the graphics of the measurement that owns `lineGraphic`, for use after its geometry was
 * edited. Returns the list unchanged when no entry holds that line, and a new array otherwise so
 * the derived result list picks the change up.
 */
export const replaceMeasurementGraphics = <T extends MeasureEntry>(
    current: Array<T>,
    lineGraphic: unknown,
    graphics: Array<unknown>
) => {
    const index = current.findIndex((entry) => Array.isArray(entry) && entry.includes(lineGraphic));
    if (index === -1) return current;

    const next = [...current];
    next[index] = graphics as T;

    return next;
};
